import MangaApiService from '../../services/MangaApiService';

export const unwrapResponse = (response) => {
    if (!response) {
        return null;
    }
    if (response.type === 'success' && response.data !== undefined) {
        return response.data;
    }
    return response.data !== undefined ? response.data : response;
};

export const getTeamId = (team) => team?.id || team?.teamId || team?.team_id;
export const getMangaId = (manga) => manga?.id || manga?.mangaId || manga?.manga_id;
export const getMangaTitle = (manga) => manga?.title || manga?.name || manga?.slug || 'Без названия';

export const splitLinks = (value) => {
    if (!value) {
        return [];
    }
    return String(value)
        .split(/[\n,]+/)
        .map((entry) => entry.trim())
        .filter(Boolean);
};

export const joinLinks = (value) => {
    if (Array.isArray(value)) {
        return value.join('\n');
    }
    return value ? String(value) : '';
};

export const resolveDonationKey = (team) => {
    if (team?.donationLinks !== undefined) {
        return 'donationLinks';
    }
    if (team?.donateLinks !== undefined) {
        return 'donateLinks';
    }
    if (team?.donations !== undefined) {
        return 'donations';
    }
    return 'donationLinks';
};

export const resolveDonationLinks = (team) => {
    if (!team) {
        return [];
    }
    return team.donationLinks || team.donateLinks || team.donations || [];
};

export const unwrapList = (response) => {
    if (!response) {
        return [];
    }
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    if (Array.isArray(payload?.items)) {
        return payload.items;
    }
    if (Array.isArray(payload?.content)) {
        return payload.content;
    }
    if (Array.isArray(payload?.data?.items)) {
        return payload.data.items;
    }
    if (Array.isArray(payload?.data?.content)) {
        return payload.data.content;
    }
    return [];
};

// Rules page and moderation "abandoned" filter both use this period.
export const MANGA_INACTIVITY_DAYS = 90;

const TEAM_MANGA_PAGE_SIZE = 50;
const TEAM_MANGA_MAX_PAGES = 50;

const mapMangaEntries = (entries) => (
    (entries || [])
        .map((entry) => entry?.manga || entry?.mangaEntity || entry?.mangaItem || entry)
        .filter(Boolean)
);

const getMangaKey = (manga) => {
    const id = getMangaId(manga);
    if (id !== undefined && id !== null && id !== '') {
        return `id:${id}`;
    }
    const slug = manga?.slug;
    if (slug) {
        return `slug:${slug}`;
    }
    return null;
};

const normalizeMangaPage = (response, requestedPage, pageSize) => {
    if (!response) {
        return { items: [], last: true, page: requestedPage };
    }
    const payload = response?.data ?? response;
    if (Array.isArray(payload)) {
        return { items: payload, last: true, page: requestedPage };
    }
    const items = unwrapList(response);
    const metaCandidate = (payload
        && !Array.isArray(payload)
        && payload.data
        && !Array.isArray(payload.data)
        && typeof payload.data === 'object'
        && (
            payload.data.page !== undefined
            || payload.data.number !== undefined
            || payload.data.totalPages !== undefined
            || payload.data.total_pages !== undefined
            || payload.data.last !== undefined
        )) ? payload.data : payload;
    const pageValue = Number(metaCandidate?.number ?? metaCandidate?.page ?? requestedPage ?? 0);
    const totalPages = Number(metaCandidate?.totalPages ?? metaCandidate?.total_pages ?? 0);
    const last = metaCandidate?.last
        ?? (totalPages ? pageValue >= totalPages - 1 : items.length < pageSize);
    return { items, last, page: pageValue };
};

export const compareMangaTitles = (left, right) => (
    getMangaTitle(left).localeCompare(getMangaTitle(right), 'ru', { sensitivity: 'base', numeric: true })
);

// The team endpoint is paged (20 by default), so walk every page instead of showing only the first one.
export const fetchAllTeamMangas = async (teamId, options = {}) => {
    const collected = [];
    const seen = new Set();
    let page = 0;
    let last = false;
    while (!last && page < TEAM_MANGA_MAX_PAGES) {
        const response = await MangaApiService.getTeamMangas(teamId, {
            ...options,
            page,
            size: TEAM_MANGA_PAGE_SIZE,
        });
        const { items, last: pageLast } = normalizeMangaPage(response, page, TEAM_MANGA_PAGE_SIZE);
        let added = 0;
        mapMangaEntries(items).forEach((manga) => {
            const key = getMangaKey(manga);
            if (key) {
                if (seen.has(key)) {
                    return;
                }
                seen.add(key);
            }
            collected.push(manga);
            added += 1;
        });
        last = pageLast || added === 0;
        page += 1;
    }
    return collected.sort(compareMangaTitles);
};

export const extractUploadMeta = (response, fallbackName) => {
    const payload = response?.data || response;
    const data = payload?.data || payload;
    const fileId = data?.id || data?.fileId || data?.file_id || payload?.id || payload?.fileId;
    const bucket = data?.bucket || data?.bucketName || data?.bucket_name;
    return {
        id: fileId,
        fileId,
        bucket,
        name: fallbackName || data?.name,
    };
};

export const normalizeStatus = (status) => String(status || '').trim().toLowerCase();

export const translateStatus = (status) => {
    const normalized = normalizeStatus(status);
    if (!normalized) {
        return 'Неизвестно';
    }
    switch (normalized) {
        case 'pending':
            return 'На модерации';
        case 'approved':
            return 'Одобрено';
        case 'published':
            return 'Опубликовано';
        case 'rejected':
            return 'Отклонено';
        case 'draft':
            return 'Черновик';
        case 'active':
            return 'Активно';
        case 'inactive':
            return 'Отключено';
        case 'deleted':
        case 'soft_deleted':
        case 'soft-deleted':
        case 'removed':
            return 'Удалено';
        default:
            return status;
    }
};

const TEAM_ROLE_LABELS = { OWNER: 'Владелец', MEMBER: 'Участник', ADMIN: 'Администратор', EDITOR: 'Редактор' };

export const translateTeamRole = (role) => {
    const key = String(role || '').toUpperCase();
    return TEAM_ROLE_LABELS[key] || (key ? key.toLowerCase() : '');
};

const AUDIT_ACTION_LABELS = {
    CHAPTER_CREATE: 'Создана глава',
    CHAPTER_DELETE: 'Удалена глава',
    CHAPTER_PAGES_APPEND: 'Добавлены страницы главы',
    CHAPTER_PAGES_UPDATE: 'Изменены страницы главы',
    CHAPTER_RESTORE: 'Восстановлена глава',
    CHAPTER_UPDATE: 'Изменена глава',
    MANGA_CREATE: 'Создан тайтл',
    MANGA_DELETE: 'Удалён тайтл',
    MANGA_RESTORE: 'Восстановлен тайтл',
    MANGA_TRANSFER_IN: 'Тайтл передан команде',
    MANGA_TRANSFER_OUT: 'Тайтл передан другой команде',
    MANGA_UPDATE: 'Изменён тайтл',
    MEMBER_INVITE: 'Добавлен участник',
    MEMBER_LEAVE: 'Участник вышел из команды',
    MEMBER_REMOVE: 'Участник удалён',
    OWNER_TRANSFER: 'Передано владение командой',
    TEAM_CREATE: 'Команда создана',
    TEAM_DELETE: 'Команда удалена',
    TEAM_UPDATE: 'Изменён профиль команды',
};

export const translateAuditAction = (action) => AUDIT_ACTION_LABELS[String(action || '').toUpperCase()] || 'Событие';

// Audit details look like "mangaId=5, chapterId=9"; show them with Russian keys.
export const formatAuditDetails = (details) => {
    if (!details) {
        return '';
    }
    const text = typeof details === 'string'
        ? details
        : Object.entries(details).map(([key, value]) => `${key}=${value}`).join(', ');
    return text
        .replace(/\bmangaId=/g, 'тайтл #')
        .replace(/\bchapterId=/g, 'глава #')
        .replace(/\bteamId=/g, 'команда #')
        .replace(/\b(userId|memberId|targetId)=/g, 'пользователь ')
        .replace(/\bfromTeamId=/g, 'из команды #')
        .replace(/\btoTeamId=/g, 'в команду #');
};

export const isRejectedStatus = (status) => normalizeStatus(status) === 'rejected';

export const isDeletedStatus = (status) => {
    const normalized = normalizeStatus(status);
    return normalized === 'deleted'
        || normalized === 'soft_deleted'
        || normalized === 'soft-deleted'
        || normalized === 'removed';
};

export const getRejectionReason = (item) => (
    item?.rejectionReason
    || item?.rejection_reason
    || item?.rejectReason
    || item?.reject_reason
    || item?.moderationReason
    || item?.moderation_reason
    || item?.reason
    || null
);

const STATUS_MESSAGES = {
    400: 'Проверьте введённые данные.',
    401: 'Сессия истекла. Войдите снова.',
    403: 'Недостаточно прав для этого действия.',
    404: 'Не найдено. Возможно, это удалили или у вас нет доступа.',
    409: 'Действие сейчас недоступно: данные изменились. Обновите страницу.',
    413: 'Файл слишком большой. Уменьшите размер и попробуйте снова.',
    415: 'Неподдерживаемый тип файла. Допустимы: JPEG, PNG, WEBP.',
    422: 'Некорректные данные. Проверьте файл или поля формы.',
};

const hasCyrillic = (value) => /[а-яё]/i.test(String(value || ''));

/**
 * Turns an axios/service error into Russian text for the user. The manga API answers errors without a body,
 * so the status code decides; `overrides` maps a status to a screen-specific message.
 */
export const getMangaErrorMessage = (error, fallbackMessage, overrides = {}) => {
    const status = error?.response?.status;
    if (status && overrides[status]) {
        return overrides[status];
    }
    if (error?.rateLimit) {
        return error.message;
    }
    const serverMessage = error?.response?.data?.message;
    if (hasCyrillic(serverMessage)) {
        return serverMessage;
    }
    if (status && STATUS_MESSAGES[status]) {
        return STATUS_MESSAGES[status];
    }
    if (status >= 500) {
        return 'Сервер манги временно недоступен. Попробуйте позже.';
    }
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {
        return 'Нет связи с сервером манги. Проверьте интернет и попробуйте снова.';
    }
    if (!error?.response && hasCyrillic(error?.message)) {
        return error.message;
    }
    return fallbackMessage;
};

export const getImageUploadErrorMessage = (error, fallbackMessage) => {
    const status = error?.response?.status;
    if (status === 413) {
        return 'Файл слишком большой. Уменьшите размер и попробуйте снова.';
    }
    if (status === 415) {
        return 'Неподдерживаемый тип изображения. Допустимы: JPEG, PNG, WEBP.';
    }
    if (status === 422) {
        return 'Некорректные данные изображения. Файл поврежден или не поддерживается.';
    }
    const message = error?.response?.data?.message || error?.message || '';
    const normalized = String(message).toLowerCase();
    if (
        normalized.includes('too large')
        || normalized.includes('payload too large')
        || normalized.includes('entity too large')
    ) {
        return 'Файл слишком большой. Уменьшите размер и попробуйте снова.';
    }
    if (
        error?.code === 'ERR_NETWORK'
        && typeof FormData !== 'undefined'
        && error?.config?.data instanceof FormData
    ) {
        return 'Не удалось загрузить файл. Возможно, он слишком большой.';
    }
    return getMangaErrorMessage(error, fallbackMessage);
};
